/**
 * Dialog Manager
 *
 * Centralized management for dialogs, modals, and floating panels.
 * Handles focus management, z-index stacking, and keyboard interactions.
 *
 * Features:
 * - Dialog registration and lifecycle management
 * - Escape key handling (closes topmost dialog)
 * - Z-index management for proper stacking
 * - Focus trap and restoration
 * - Event-based communication
 *
 * @module utils/dialogManager
 */

import { Z_INDEX } from './zIndexLayers';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

/**
 * Dialog types for different UI components
 */
export const DIALOG_TYPES = {
  MODAL: 'modal',              // Full modal dialog (e.g., CroppedMeasurementsDialog)
  PANEL: 'panel',              // Floating panel (e.g., Corrosion Panel, Measurements Panel)
  SIDEBAR: 'sidebar',          // Sidebar panel (e.g., Display Settings)
  INSTRUCTIONS: 'instructions' // Instruction overlays (e.g., Cylinder Instructions)
};

/**
 * DialogManager class for managing multiple dialogs
 */
export class DialogManager {
  constructor() {
    // Stack of active dialogs (most recent at the end)
    this.dialogStack = [];

    // Keyboard event handler reference
    this.keyboardHandler = null;

    // Previous focus element for restoration
    this.previousFocusElement = null;

    // Event listeners for dialog state changes
    this.listeners = {
      opened: [],
      closed: [],
      stackChanged: []
    };

    // Initialize keyboard listener
    this.setupKeyboardListener();
  }

  /**
   * Register a new dialog
   *
   * @param {Object} options - Dialog configuration
   * @param {string} options.id - Unique dialog identifier
   * @param {string} options.type - Dialog type (from DIALOG_TYPES)
   * @param {Function} options.onClose - Callback to close the dialog
   * @param {number} options.baseZIndex - Base z-index for this dialog type
   * @param {boolean} options.closeOnEscape - Whether ESC key should close this dialog (default: true)
   * @param {boolean} options.trapFocus - Whether to trap focus within dialog (default: false)
   * @returns {Object} Dialog registration object
   */
  registerDialog(options) {
    const {
      id,
      type,
      onClose,
      baseZIndex = Z_INDEX.APP_MODAL,
      closeOnEscape = true,
      trapFocus = false
    } = options;

    // Validate required options
    if (!id || !type || !onClose) {
      debugError('DialogManager', 'Missing required options:', { id, type, onClose });
      return null;
    }

    // Check if dialog already registered
    const existingDialog = this.dialogStack.find(d => d.id === id);
    if (existingDialog) {
      debugWarn('DialogManager', `Dialog ${id} already registered`);
      return existingDialog;
    }

    // Store previous focus element if this is the first dialog
    if (this.dialogStack.length === 0) {
      this.previousFocusElement = document.activeElement;
    }

    // Create dialog registration object
    const dialog = {
      id,
      type,
      onClose,
      baseZIndex,
      closeOnEscape,
      trapFocus,
      registeredAt: Date.now()
    };

    // Add to stack
    this.dialogStack.push(dialog);

    // Calculate z-index based on stack position
    const zIndex = this.calculateZIndex(dialog);

    debugLog('DialogManager', `Registered dialog: ${id} (type: ${type}, z-index: ${zIndex})`);

    // Notify listeners
    this.emit('opened', { dialog, zIndex });
    this.emit('stackChanged', { stack: [...this.dialogStack] });

    return {
      dialog,
      zIndex,
      unregister: () => this.unregisterDialog(id)
    };
  }

  /**
   * Unregister a dialog
   *
   * @param {string} id - Dialog identifier
   */
  unregisterDialog(id) {
    const index = this.dialogStack.findIndex(d => d.id === id);
    if (index === -1) {
      debugWarn('DialogManager', `Dialog ${id} not found`);
      return;
    }

    const dialog = this.dialogStack[index];
    this.dialogStack.splice(index, 1);

    debugLog('DialogManager', `Unregistered dialog: ${id}`);

    // Restore focus if this was the last dialog
    if (this.dialogStack.length === 0 && this.previousFocusElement) {
      try {
        this.previousFocusElement.focus();
      } catch (error) {
        debugWarn('DialogManager', 'Could not restore focus:', error);
      }
      this.previousFocusElement = null;
    }

    // Notify listeners
    this.emit('closed', { dialog });
    this.emit('stackChanged', { stack: [...this.dialogStack] });
  }

  /**
   * Get the topmost dialog
   *
   * @returns {Object|null} Topmost dialog or null if none
   */
  getTopmostDialog() {
    return this.dialogStack.length > 0
      ? this.dialogStack[this.dialogStack.length - 1]
      : null;
  }

  /**
   * Check if a dialog is the topmost one
   *
   * @param {string} id - Dialog identifier
   * @returns {boolean} True if dialog is topmost
   */
  isTopmostDialog(id) {
    const topmost = this.getTopmostDialog();
    return topmost && topmost.id === id;
  }

  /**
   * Calculate z-index for a dialog based on its type and stack position
   *
   * @param {Object} dialog - Dialog object
   * @returns {number} Calculated z-index
   */
  calculateZIndex(dialog) {
    // Find the position of this dialog in the stack
    const index = this.dialogStack.findIndex(d => d.id === dialog.id);

    // Use base z-index + stack offset
    return dialog.baseZIndex + index;
  }

  /**
   * Get current z-index for a registered dialog
   *
   * @param {string} id - Dialog identifier
   * @returns {number|null} Z-index or null if not found
   */
  getDialogZIndex(id) {
    const dialog = this.dialogStack.find(d => d.id === id);
    return dialog ? this.calculateZIndex(dialog) : null;
  }

  /**
   * Close the topmost dialog
   */
  closeTopmostDialog() {
    const topmost = this.getTopmostDialog();
    if (topmost && topmost.closeOnEscape) {
      debugLog('DialogManager', `Closing topmost dialog: ${topmost.id}`);
      topmost.onClose();
    }
  }

  /**
   * Close all dialogs
   */
  closeAllDialogs() {
    debugLog('DialogManager', 'Closing all dialogs');
    // Close in reverse order (topmost first)
    const dialogs = [...this.dialogStack].reverse();
    dialogs.forEach(dialog => {
      dialog.onClose();
    });
  }

  /**
   * Setup keyboard event listener for Escape key
   */
  setupKeyboardListener() {
    this.keyboardHandler = (event) => {
      // Handle Escape key
      if (event.key === 'Escape' || event.keyCode === 27) {
        // Only handle if not typing in input
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
          return;
        }

        // Close topmost dialog
        if (this.dialogStack.length > 0) {
          event.preventDefault();
          event.stopPropagation();
          this.closeTopmostDialog();
        }
      }
    };

    document.addEventListener('keydown', this.keyboardHandler, true);
  }

  /**
   * Remove keyboard event listener
   */
  removeKeyboardListener() {
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler, true);
      this.keyboardHandler = null;
    }
  }

  /**
   * Subscribe to dialog events
   *
   * @param {string} event - Event name ('opened', 'closed', 'stackChanged')
   * @param {Function} callback - Event callback
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.listeners[event]) {
      debugWarn('DialogManager', `Unknown event: ${event}`);
      return () => {};
    }

    this.listeners[event].push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.listeners[event].indexOf(callback);
      if (index > -1) {
        this.listeners[event].splice(index, 1);
      }
    };
  }

  /**
   * Emit an event to all listeners
   *
   * @param {string} event - Event name
   * @param {Object} data - Event data
   */
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          debugError('DialogManager', 'Error in event listener:', error);
        }
      });
    }
  }

  /**
   * Get current dialog stack state
   *
   * @returns {Array} Current dialog stack
   */
  getStack() {
    return [...this.dialogStack];
  }

  /**
   * Check if any dialogs are open
   *
   * @returns {boolean} True if dialogs are open
   */
  hasOpenDialogs() {
    return this.dialogStack.length > 0;
  }

  /**
   * Get count of open dialogs
   *
   * @returns {number} Number of open dialogs
   */
  getDialogCount() {
    return this.dialogStack.length;
  }

  /**
   * Cleanup all resources
   */
  destroy() {
    this.removeKeyboardListener();
    this.dialogStack = [];
    this.listeners = { opened: [], closed: [], stackChanged: [] };
    this.previousFocusElement = null;
  }
}

// Singleton instance
let dialogManagerInstance = null;

/**
 * Get DialogManager singleton instance
 *
 * @returns {DialogManager} DialogManager instance
 */
export function getDialogManager() {
  if (!dialogManagerInstance) {
    dialogManagerInstance = new DialogManager();
  }
  return dialogManagerInstance;
}

/**
 * Reset singleton instance (useful for testing)
 */
export function resetDialogManager() {
  if (dialogManagerInstance) {
    dialogManagerInstance.destroy();
  }
  dialogManagerInstance = null;
}

// Export for convenience
export default {
  DialogManager,
  DIALOG_TYPES,
  getDialogManager,
  resetDialogManager
};
